import { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useParams, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import yup from 'utils/yup';
import { createAccessRequest } from 'bc-artifacts-client/api';
import type { AccessRequestAccessType, BriefAccessRoleType, MembershipRoleType } from 'bc-model';
import { ODSButton, ODSGroup, ODSInput } from 'bc-ods';
import RequestSuccessAlert from './AccessRequests/shared/RequestSuccessAlert';
import useAccessRequestStore from 'stores/accessRequests/useAccessRequestStore';
import useAccountStore from 'stores/useAccountStore';
import RoleDropdown from './Brief/PlanningTeam/RoleDropdown';
import { parseBriefIdFromBriefKeyParam } from 'utils/briefUrlUtils';

const useParseAccessInfoFromParams = (): {
  type: AccessRequestAccessType | undefined;
  id: number | undefined;
} => {
  const { briefKey, groupId, idString } = useParams();
  if (briefKey) {
    const id = parseBriefIdFromBriefKeyParam(briefKey, true);
    return { type: 'brief', id };
  }
  if (groupId) {
    return { type: 'group', id: parseInt(groupId, 10) };
  }
  if (idString) {
    return { type: 'organization', id: parseInt(idString, 10) };
  }
  return { type: undefined, id: undefined };
};

const parseRequestSubmission = (
  accountId: number | undefined,
  accountUuid: string | undefined,
  accessInfo: Required<{ type: AccessRequestAccessType; id: number }>,
  formData: { note: string; requestedRole: Exclude<BriefAccessRoleType, 'owner'> }
): RequestAccessFormCreateType | null => {
  const baseFields = {
    ...(accountUuid ? { accountUuid } : { accountId }),
    accessType: accessInfo.type,
    accessTypeId: accessInfo.id,
    note: formData.note,
  };
  if (accessInfo.type === 'brief') {
    return {
      ...baseFields,
      requestedRole: formData.requestedRole,
      accessType: 'brief',
    };
  }
  if (accessInfo.type === 'organization' || accessInfo.type === 'group') {
    return {
      ...baseFields,
      accessType: accessInfo.type,
      requestedRole: 'member',
    };
  }
  return null;
};

const messages = defineMessages({
  invalidNote: {
    id: 'RequestAccessForm.invalidNote',
    defaultMessage: `Note must be {maxNoteLength} characters or less`,
  },
  requestAccess: {
    id: 'RequestAccessForm.requestAccess',
    defaultMessage: 'Request access',
  },
  goBack: {
    id: 'RequestAccessForm.goBack',
    defaultMessage: 'Go back',
  },
  requestAccessTitle: {
    id: 'ErrorMessage.requestAccessTitle',
    defaultMessage: 'You need access',
  },
  requestAccessDescription: {
    id: 'ErrorMessage.requestAccessDescription',
    defaultMessage:
      'Ask for access to this {accessType, select, organization {organization} group {group} brief {plan} other {item}} or log into an account with access.',
  },
  noteLabel: {
    id: 'RequestAccessForm.noteLabel',
    defaultMessage: 'Message (required)',
  },
  roleLabel: {
    id: 'RequestAccessForm.roleLabel',
    defaultMessage: 'Role',
  },
  successText: {
    id: 'RequestAccessForm.successText',
    defaultMessage: 'Access request sent',
  },
  error: {
    id: 'RequestAccessForm.error',
    defaultMessage: 'Error: {error}',
  },
});

const MAX_NOTE_LENGTH = 500;

type RequestAccessFormCreateType = {
  note: string;
  accountId?: number;
  accountUuid?: string;
  accessTypeId: number;
} & (
  | {
      accessType: 'brief';
      requestedRole: Exclude<BriefAccessRoleType, 'owner'>;
    }
  | {
      accessType: 'group' | 'organization';
      requestedRole: Extract<MembershipRoleType, 'member'>;
    }
);

type Props = {
  onComplete?: () => void;
};

const RequestAccessForm: React.FC<Props> = ({ onComplete }) => {
  const intl = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const accessInfo = useParseAccessInfoFromParams();
  const account = useAccountStore((state) => state.account);

  const formValidationSchema = yup.object().shape({
    note: yup.string().max(MAX_NOTE_LENGTH).required(),
  });

  const submissionValidationSchema = yup
    .object<RequestAccessFormCreateType>()
    .shape({
      accountId: yup.number().optional(),
      accountUuid: yup.string().optional(),
      accessType: yup.string().oneOf(['brief', 'group', 'organization']).required(),
      accessTypeId: yup.number().required(),
      requestedRole: yup.lazy(() =>
        accessInfo.type === 'brief'
          ? yup.string().oneOf(['admin', 'editor', 'reader']).required()
          : yup.string().oneOf(['member']).required()
      ),
      note: yup.string().max(MAX_NOTE_LENGTH).required(),
    })
    .test(
      'has-account-identifier',
      'Either accountId or accountUuid is required',
      (value) => value?.accountId != null || value?.accountUuid != null
    );
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (formData: {
      note: string;
      requestedRole: Exclude<BriefAccessRoleType, 'owner'>;
    }): Promise<RequestAccessFormCreateType | null> => {
      try {
        if (!accessInfo.type || !accessInfo.id || !account) {
          setError('Missing required info: access type/id or account id');
          return null;
        }

        if (Number.isNaN(accessInfo.id)) {
          setError('Invalid access type ID');
          return null;
        }

        const submission: RequestAccessFormCreateType | null = parseRequestSubmission(
          account.id,
          account.uuid || undefined,
          accessInfo as Required<{ type: AccessRequestAccessType; id: number }>,
          formData
        );

        if (!submission) {
          return null;
        }

        const validSubmission = await submissionValidationSchema.validate(submission);

        if (!submission?.accessType) {
          return null;
        }
        const res = await createAccessRequest(validSubmission);
        if (res.status === 200) {
          useAccessRequestStore.setState((state) => {
            state.accessRequests[res.data.id] = res.data;
            return state;
          });
          setError(undefined);
          onComplete?.();
          setSubmittedSuccessfully(true);
          setShowSuccess(true);
        }
        return validSubmission as RequestAccessFormCreateType;
      } catch (err) {
        setShowSuccess(false);
        switch (true) {
          case err instanceof AxiosError:
            setError(err.response?.data?.error);
            break;
          case err instanceof Error:
            break;
          default:
            setError('An unknown error occurred while submitting the request');
            break;
        }
        return null;
      }
    },
    [accessInfo, account, submissionValidationSchema, onComplete]
  );

  if (!account || !accessInfo.type) return null;
  return (
    <>
      <h1 className="mb-0">
        <FormattedMessage {...messages.requestAccessTitle} />
      </h1>
      <div className="text-black">
        <FormattedMessage
          {...messages.requestAccessDescription}
          values={{ accessType: accessInfo.type }}
        />
      </div>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          note: '',
          requestedRole: 'editor', // only used for brief access requests
        }}
        validationSchema={formValidationSchema}
        validateOnMount={true}
        initialErrors={{ note: 'Pre-initial validation' }} // ensures the submit button is disabled on load
      >
        {({ handleChange, isValid, setFieldValue, values }) => (
          <Form>
            {showSuccess && (
              <RequestSuccessAlert
                hide={() => {
                  setShowSuccess(false);
                }}
                content={<FormattedMessage {...messages.successText} />}
              />
            )}
            {error && (
              <div className="alert alert-danger">
                <FormattedMessage {...messages.error} values={{ error }} />
              </div>
            )}
            {
              <ODSGroup stacked verticalAlign="start">
                <ODSInput
                  className="w-100"
                  name="note"
                  label={<FormattedMessage {...messages.noteLabel} />}
                  invalid={values.note?.length > MAX_NOTE_LENGTH}
                  errorMessage={
                    <FormattedMessage
                      {...messages.invalidNote}
                      values={{ maxNoteLength: MAX_NOTE_LENGTH }}
                    />
                  }
                  type="textarea"
                  autoResize
                  value={values.note}
                  onChange={handleChange}
                  disabled={submittedSuccessfully}
                />
                {accessInfo.type === 'brief' && (
                  <div css={{ width: '8rem' }} data-testid="access-role-dropdown">
                    <RoleDropdown
                      label={intl.formatMessage(messages.roleLabel)}
                      size="sm"
                      role={values.requestedRole}
                      setRole={async (role) => setFieldValue('requestedRole', role)}
                    />
                  </div>
                )}
              </ODSGroup>
            }

            <ODSGroup align="start" className={'py-2'}>
              <ODSButton
                outline
                onClick={() => {
                  if (accessInfo.type === 'brief') {
                    navigate(-1);
                  } else {
                    /**
                     * Org/group screens push users to an intermediary route
                     * before the request access screen is activated. To avoid
                     * being infinitely stuck in the request access flow, we go back two steps.
                     */
                    navigate(-2);
                  }
                }}
              >
                {intl.formatMessage(messages.goBack)}
              </ODSButton>
              <ODSButton cta type="submit" disabled={!isValid || submittedSuccessfully}>
                {intl.formatMessage(messages.requestAccess)}
              </ODSButton>
            </ODSGroup>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RequestAccessForm;
